import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { NOTIFICATION } from "../../config/constants";
import { Get } from "../../config/api/get";

const MAX_LATEST = 10;

const initialState = {
  count: 0,
  loading: false,
  error: null,
  latestNotifications: [],
  socketConnected: false,
};

export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (token) => {
    if (!token) {
      return { count: 0, latestNotifications: [] };
    }

    const response = await Get(NOTIFICATION.getUnreadAdminNotifications, token);
    if (response?.status) {
      const notifications = response?.data?.notifications || [];
      const count =
        response?.data?.totalUnreadCount ??
        response?.data?.count ??
        notifications.length;

      return {
        count,
        latestNotifications: notifications.filter(
          (item) => item?.source !== "NOTICE"
        ),
      };
    }

    return {
      count: 0,
      latestNotifications: [],
    };
  }
);

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    receiveNotification: (state, action) => {
      const notification = action.payload;
      if (!notification?._id) return;
      if (notification.source === "NOTICE") return;

      const exists = state.latestNotifications.some((item) => item._id === notification._id);
      if (exists) return;

      state.latestNotifications.unshift(notification);
      if (state.latestNotifications.length > MAX_LATEST) {
        state.latestNotifications = state.latestNotifications.slice(0, MAX_LATEST);
      }

      if (!notification.isRead) {
        state.count += 1;
      }
    },
    setNotificationReadState: (state, action) => {
      const { id, isRead } = action.payload;
      const target = state.latestNotifications.find((item) => item._id === id);

      if (target) {
        const wasRead = Boolean(target.isRead);
        target.isRead = isRead;

        if (!wasRead && isRead) {
          state.count = Math.max(0, state.count - 1);
        } else if (wasRead && !isRead) {
          state.count += 1;
        }
      }

      if (isRead) {
        state.latestNotifications = state.latestNotifications.filter((item) => !item.isRead);
      }
    },
    markNotificationReadInState: (state, action) => {
      const id = action.payload;
      const target = state.latestNotifications.find((item) => item._id === id);

      if (target && !target.isRead) {
        target.isRead = true;
        state.count = Math.max(0, state.count - 1);
      } else if (!target && state.count > 0) {
        state.count = Math.max(0, state.count - 1);
      }

      state.latestNotifications = state.latestNotifications.filter((item) => !item.isRead);
    },
    markAllNotificationsReadInState: (state) => {
      state.latestNotifications = [];
      state.count = 0;
    },
    setSocketConnected: (state, action) => {
      state.socketConnected = Boolean(action.payload);
    },
    resetNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.count = action.payload.count;
        state.latestNotifications = action.payload.latestNotifications;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.count = 0;
        state.latestNotifications = [];
      });
  },
});

export const {
  receiveNotification,
  markNotificationReadInState,
  setNotificationReadState,
  markAllNotificationsReadInState,
  setSocketConnected,
  resetNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
